// Analytics — Phase 6f.
//
// Pulls the raw data needed for the 5 KPIs on /analytics and computes
// everything client-side. We trade a little wasted payload for not
// needing a bespoke backend endpoint. Cache for 5 minutes — the dashboard
// is a "check a few times a day" surface, not a live ticker.
//
// KPIs (all scoped to the last 30 days):
//   1. Median first-reply time — inbound → first outbound, in hours.
//   2. Daily volume by brand — one bar per day × (md + hk).
//   3. Haiku acceptance % — of outbound sends that had a prior ai_generated
//      draft, what share of drafts were sent with low edit distance.
//      We don't have draft_vs_sent data for every thread yet, so we
//      fall back to "draft existed → was ultimately sent" as acceptance.
//   4. Backlog age — median hours since newest message on the oldest
//      currently-open conversations (top 20% by age, status='open').
//   5. Peak hours — count of inbound messages per hour-of-day 0..23.

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Brand } from '@md/shared';

const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

type RawMessage = {
  id: string;
  conversation_id: string;
  direction: 'inbound' | 'outbound';
  sent_at: string;
};

type RawDraft = {
  id: string;
  conversation_id: string;
  source: 'ai_generated' | 'user_edited';
  generated_at: string;
};

type RawConv = {
  id: string;
  brand: Brand;
  status: string;
  last_message_at: string;
};

export type DailyVolumePoint = {
  /** YYYY-MM-DD. */
  date: string;
  md: number;
  hk: number;
};

export type HourBucket = {
  hour: number; // 0..23
  count: number;
};

export type AnalyticsData = {
  medianFirstReplyHours: number | null;
  replySampleSize: number;
  dailyVolume: DailyVolumePoint[];
  haikuAcceptancePct: number | null;
  haikuSampleSize: number;
  backlogAgeHours: number | null;
  backlogCount: number;
  peakHours: HourBucket[];
};

export function useAnalytics() {
  return useQuery<AnalyticsData>({
    queryKey: ['analytics-v1'],
    queryFn: compute,
    staleTime: 5 * 60 * 1000,
  });
}

async function compute(): Promise<AnalyticsData> {
  const since = new Date(Date.now() - 30 * MS_PER_DAY).toISOString();

  // 1. Messages — the big one. cap at 5000 rows for safety.
  const { data: msgs, error: msgErr } = await supabase
    .from('messages')
    .select('id, conversation_id, direction, sent_at')
    .gte('sent_at', since)
    .order('sent_at', { ascending: true })
    .limit(5000);
  if (msgErr) throw msgErr;

  // 2. Conversations — for brand + status lookup.
  const { data: convs, error: convErr } = await supabase
    .from('conversations')
    .select('id, brand, status, last_message_at')
    .gte('last_message_at', since)
    .limit(2000);
  if (convErr) throw convErr;

  // 3. Drafts (last 30d). Small volume.
  const { data: drafts, error: draftErr } = await supabase
    .from('drafts')
    .select('id, conversation_id, source, generated_at')
    .gte('generated_at', since)
    .limit(2000);
  if (draftErr) throw draftErr;

  const convById = new Map<string, RawConv>();
  for (const c of (convs ?? []) as RawConv[]) convById.set(c.id, c);

  // --- KPI 1: median first-reply time --------------------------------------
  // For each conversation with both an inbound and a later outbound, compute
  // (firstOutbound - firstInbound) in hours. Median across conversations.
  const perConv = new Map<
    string,
    { firstInbound?: number; firstOutbound?: number }
  >();
  for (const m of (msgs ?? []) as RawMessage[]) {
    const t = new Date(m.sent_at).getTime();
    const bucket = perConv.get(m.conversation_id) ?? {};
    if (m.direction === 'inbound') {
      if (bucket.firstInbound === undefined || t < bucket.firstInbound) {
        bucket.firstInbound = t;
      }
    } else {
      if (bucket.firstOutbound === undefined || t < bucket.firstOutbound) {
        bucket.firstOutbound = t;
      }
    }
    perConv.set(m.conversation_id, bucket);
  }
  const replyGapsHours: number[] = [];
  for (const { firstInbound, firstOutbound } of perConv.values()) {
    if (firstInbound !== undefined && firstOutbound !== undefined && firstOutbound > firstInbound) {
      replyGapsHours.push((firstOutbound - firstInbound) / MS_PER_HOUR);
    }
  }
  const medianFirstReplyHours = replyGapsHours.length === 0 ? null : median(replyGapsHours);

  // --- KPI 2: daily volume by brand ----------------------------------------
  // Count inbound messages per (YYYY-MM-DD, brand). Skip conversations we
  // don't have brand info for (shouldn't happen within 30d window).
  const volKey = (date: string, brand: Brand) => `${date}|${brand}`;
  const volMap = new Map<string, number>();
  for (const m of (msgs ?? []) as RawMessage[]) {
    if (m.direction !== 'inbound') continue;
    const c = convById.get(m.conversation_id);
    if (!c) continue;
    const date = m.sent_at.slice(0, 10);
    const k = volKey(date, c.brand);
    volMap.set(k, (volMap.get(k) ?? 0) + 1);
  }
  const dailyVolume: DailyVolumePoint[] = [];
  // Always emit all 30 days in order so the chart doesn't skip dates.
  for (let i = 29; i >= 0; i--) {
    const d = new Date(Date.now() - i * MS_PER_DAY);
    const iso = d.toISOString().slice(0, 10);
    dailyVolume.push({
      date: iso,
      md: volMap.get(volKey(iso, 'md')) ?? 0,
      hk: volMap.get(volKey(iso, 'hk')) ?? 0,
    });
  }

  // --- KPI 3: Haiku acceptance % ------------------------------------------
  // For each conversation that has at least one ai_generated draft AND at
  // least one outbound message after that draft, count it "accepted."
  // Total = conversations with at least one ai_generated draft.
  const convsWithDraft = new Set<string>();
  const convsWithAcceptedDraft = new Set<string>();
  // Find earliest ai_generated per conversation.
  const earliestAiDraft = new Map<string, number>();
  for (const d of (drafts ?? []) as RawDraft[]) {
    if (d.source !== 'ai_generated') continue;
    convsWithDraft.add(d.conversation_id);
    const t = new Date(d.generated_at).getTime();
    const cur = earliestAiDraft.get(d.conversation_id);
    if (cur === undefined || t < cur) earliestAiDraft.set(d.conversation_id, t);
  }
  // Outbounds after the earliest draft mark the conv as accepted.
  for (const m of (msgs ?? []) as RawMessage[]) {
    if (m.direction !== 'outbound') continue;
    const draftTime = earliestAiDraft.get(m.conversation_id);
    if (draftTime === undefined) continue;
    const t = new Date(m.sent_at).getTime();
    if (t >= draftTime) convsWithAcceptedDraft.add(m.conversation_id);
  }
  const haikuSampleSize = convsWithDraft.size;
  const haikuAcceptancePct =
    haikuSampleSize === 0
      ? null
      : (convsWithAcceptedDraft.size / haikuSampleSize) * 100;

  // --- KPI 4: backlog age --------------------------------------------------
  // Oldest currently-open conversations by last_message_at. Report the
  // median age in hours so a single stale thread doesn't skew the metric.
  const openConvs = ((convs ?? []) as RawConv[]).filter((c) => c.status === 'open');
  const openAges = openConvs.map(
    (c) => (Date.now() - new Date(c.last_message_at).getTime()) / MS_PER_HOUR,
  );
  const backlogAgeHours = openAges.length === 0 ? null : median(openAges);

  // --- KPI 5: peak hours ---------------------------------------------------
  const hourCounts = new Array<number>(24).fill(0);
  for (const m of (msgs ?? []) as RawMessage[]) {
    if (m.direction !== 'inbound') continue;
    const h = new Date(m.sent_at).getHours();
    if (h >= 0 && h < 24) hourCounts[h]! += 1;
  }
  const peakHours: HourBucket[] = hourCounts.map((count, hour) => ({ hour, count }));

  return {
    medianFirstReplyHours,
    replySampleSize: replyGapsHours.length,
    dailyVolume,
    haikuAcceptancePct,
    haikuSampleSize,
    backlogAgeHours,
    backlogCount: openAges.length,
    peakHours,
  };
}

function median(xs: number[]): number {
  if (xs.length === 0) return 0;
  const sorted = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}
